import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import IconButton from '../components/IconButton';
import { LayoutDashboard, Calculator, Package, Archive, Database, Search as SearchIcon, Users as UsersIcon, Settings as SettingsIcon, User, X, BarChart3, Layers, ScrollText, Gavel, Activity, BookOpen, Award, Megaphone, MessageCircle } from 'lucide-react';

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [openTabs, setOpenTabs] = useState([]);
  const [activePath, setActivePath] = useState('/');

  // Mapeamento de rotas para títulos e ícones
  const routesConfig = {
    '/': { title: 'Dashboard', Icon: LayoutDashboard },
    '/contagem': { title: 'Contagem', Icon: Calculator },
    '/faixas': { title: 'Faixas', Icon: Layers },
    '/gruposegur': { title: 'Grupo', Icon: Layers },
    '/consolidacao': { title: 'Consolidação', Icon: Archive },
    '/regras': { title: 'Regras', Icon: Gavel },
    '/movimentacao': { title: 'Movimentação', Icon: Activity },
    '/convencao': { title: 'Convenção', Icon: ScrollText },
    '/cursos': { title: 'Cursos', Icon: BookOpen },
    '/cert-modelos': { title: 'Modelos', Icon: Award },
    '/emitir-certificados': { title: 'Emissão', Icon: Award },
    '/participantes': { title: 'Participantes', Icon: UsersIcon },
    '/cliente': { title: 'Cliente', Icon: User },
    
    '/lancamentos': { title: 'Lançamentos', Icon: Package },
    
    
    
    '/relatorio': { title: 'Relatório', Icon: BarChart3 },
    '/demandas': { title: 'Demandas', Icon: LayoutDashboard },
    '/marketing': { title: 'Marketing', Icon: Megaphone },
    '/marketing/campanhas': { title: 'Campanhas', Icon: Megaphone },
    '/marketing/contatos': { title: 'Contatos de Marketing', Icon: UsersIcon },
    '/marketing/grupos': { title: 'Grupos de Marketing', Icon: UsersIcon },
    '/marketing/mensagens': { title: 'Mensagens', Icon: MessageCircle },
    '/marketing/relatorios': { title: 'Relatórios de Marketing', Icon: BarChart3 },
    '/marketing/configuracoes': { title: 'Configurações de Marketing', Icon: SettingsIcon },
    '/profile': { title: 'Perfil', Icon: User },
    '/perfil': { title: 'Perfil', Icon: User },
    '/configuracoes': { title: 'Configurações', Icon: SettingsIcon },
    '/usuarios': { title: 'Usuários', Icon: UsersIcon },
    
    '/change-password': { title: 'Alterar Senha', Icon: SettingsIcon },
  };

  // Garantir que a rota atual esteja nas abas abertas
  useEffect(() => {
    const rawPath = location.pathname;
    const path = rawPath === '/dashboard' ? '/' : rawPath; // normaliza redirecionamento
    setActivePath(path);
    setOpenTabs(prev => {
      if (prev.some(tab => tab.path === path)) return prev;
      // Rotas com parâmetro dinâmico (ex.: /cert-modelos/:id/editar)
      let config = routesConfig[path];
      if (!config && /^\/cert-modelos\/[^/]+\/editar$/.test(path)) {
        config = { title: 'Layout', Icon: Award };
      }
      config = config || { title: 'Página', Icon: LayoutDashboard };
      return [...prev, { path, title: config.title, Icon: config.Icon }];
    });
  }, [location.pathname]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleTabClick = (path) => {
    setActivePath(path);
    navigate(path);
  };

  const handleCloseTab = (path) => {
    setOpenTabs(prev => {
      const filtered = prev.filter(tab => tab.path !== path);
      // Se a aba fechada era a ativa, navegar para a última restante após atualizar o estado
      if (activePath === path) {
        const nextTab = filtered.slice(-1)[0];
        // navega após o próximo tick para garantir que o estado foi aplicado
        setTimeout(() => {
          navigate(nextTab ? nextTab.path : '/');
        }, 0);
      }
      return filtered;
    });
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <Header onToggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
        
        {/* Tabs Bar */}
        <div className="px-6 pt-4 bg-gray-50 dark:bg-gray-900">
          <div className="w-full">
            <div className="flex items-center space-x-2 overflow-x-auto no-scrollbar">
              {openTabs.map(({ path, title, Icon }) => {
                const isActive = activePath === path;
                return (
                  <div
                    key={path}
                    className={`flex items-center px-3 py-2 rounded-md border ${isActive ? 'bg-white dark:bg-gray-800 border-primary-500 shadow' : 'bg-gray-100 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'} cursor-pointer select-none`}
                    onClick={() => handleTabClick(path)}
                  >
                    {Icon && <Icon className={`w-4 h-4 mr-2 ${isActive ? 'text-primary-600' : 'text-gray-500 dark:text-gray-400'}`} />}
                    <span className={`text-sm ${isActive ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-700 dark:text-gray-300'}`}>{title}</span>
                    {/* Close button */}
                    {path !== '/' && (
                      <IconButton
                        icon={X}
                        onClick={(e) => { e.stopPropagation(); handleCloseTab(path); }}
                        variant="secondary"
                        size="xs"
                        title="Fechar aba"
                        className="ml-2 opacity-60 hover:opacity-100 shrink-0"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 p-6 transition-colors duration-200">
          <div className="w-full">
            <Outlet />
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default DashboardLayout;
